import type { MedicalConsultation } from '../../../types/api';

type ConsultationSpecialtyData = Record<string, unknown>;

export const CONSULTATION_SPECIALTY_KEY = {
  GENERAL: 'general',
  PEDIATRICS: 'pediatrics',
  INTERNAL_MEDICINE: 'internal_medicine',
  TRAUMATOLOGY: 'traumatology',
  DERMATOLOGY: 'dermatology',
  OPHTHALMOLOGY: 'ophthalmology',
  CARDIOLOGY: 'cardiology',
  GYNECOLOGY: 'gynecology',
  SURGERY: 'surgery',
} as const;

export type ConsultationSpecialtyKey = (typeof CONSULTATION_SPECIALTY_KEY)[keyof typeof CONSULTATION_SPECIALTY_KEY];

const SPECIALTY_LABEL_MATCHERS: Array<{ key: ConsultationSpecialtyKey; tokens: string[] }> = [
  { key: CONSULTATION_SPECIALTY_KEY.PEDIATRICS, tokens: ['pediatr'] },
  { key: CONSULTATION_SPECIALTY_KEY.INTERNAL_MEDICINE, tokens: ['medicina interna', 'internista'] },
  { key: CONSULTATION_SPECIALTY_KEY.TRAUMATOLOGY, tokens: ['traumat', 'ortoped'] },
  { key: CONSULTATION_SPECIALTY_KEY.DERMATOLOGY, tokens: ['dermat'] },
  { key: CONSULTATION_SPECIALTY_KEY.OPHTHALMOLOGY, tokens: ['oftalm', 'ocul'] },
  { key: CONSULTATION_SPECIALTY_KEY.CARDIOLOGY, tokens: ['cardio'] },
  { key: CONSULTATION_SPECIALTY_KEY.GYNECOLOGY, tokens: ['ginec', 'obstet'] },
  { key: CONSULTATION_SPECIALTY_KEY.SURGERY, tokens: ['cirug', 'quirurg'] },
];

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

const hasSpecialtyDataKey = (data: ConsultationSpecialtyData | null | undefined, key: string): boolean => {
  if (!data || typeof data !== 'object') return false;
  return key in data;
};

const resolveBySpecialtyData = (data?: ConsultationSpecialtyData): ConsultationSpecialtyKey | null => {
  if (!data) return null;

  if (hasSpecialtyDataKey(data, 'pediatric')) return CONSULTATION_SPECIALTY_KEY.PEDIATRICS;
  if (hasSpecialtyDataKey(data, 'internal_medicine')) return CONSULTATION_SPECIALTY_KEY.INTERNAL_MEDICINE;
  if (hasSpecialtyDataKey(data, 'traumatologia')) return CONSULTATION_SPECIALTY_KEY.TRAUMATOLOGY;
  if (hasSpecialtyDataKey(data, 'dermatologia')) return CONSULTATION_SPECIALTY_KEY.DERMATOLOGY;
  if (hasSpecialtyDataKey(data, 'oftalmologia')) return CONSULTATION_SPECIALTY_KEY.OPHTHALMOLOGY;
  if (hasSpecialtyDataKey(data, 'hallazgos_ginecologicos')) return CONSULTATION_SPECIALTY_KEY.GYNECOLOGY;

  return null;
};

const resolveByDoctorSpecialtyName = (specialtyName?: string): ConsultationSpecialtyKey | null => {
  if (!specialtyName) return null;

  const normalizedSpecialtyName = normalize(specialtyName);

  const match = SPECIALTY_LABEL_MATCHERS.find(({ tokens }) =>
    tokens.some((token) => normalizedSpecialtyName.includes(token))
  );

  return match?.key ?? null;
};

export function resolveConsultationSpecialtyKey(
  consultation: Pick<MedicalConsultation, 'doctor' | 'specialty_data'>
): ConsultationSpecialtyKey {
  const specialtyDataMatch = resolveBySpecialtyData(consultation.specialty_data);
  if (specialtyDataMatch) return specialtyDataMatch;

  const doctorSpecialtyMatch = resolveByDoctorSpecialtyName(consultation.doctor?.specialty?.name);
  if (doctorSpecialtyMatch) return doctorSpecialtyMatch;

  return CONSULTATION_SPECIALTY_KEY.GENERAL;
}
